/**
 * McpLimitsBox — límites de seguridad MCP del usuario + solicitud de aumento.
 * Se muestra dentro del panel "Conexiones MCP".
 */
import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Loader2, Send, HelpCircle, Clock, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { mcpService } from '../../services/mcpService';

const HELP = {
  box: 'Topes de seguridad que aplican SÓLO a lo que hace un agente MCP (Claude, '
    + 'ChatGPT, Codex…). Lo que vos hacés desde la interfaz de Studio Media Labs no '
    + 'tiene estos límites.',
  max_nodes_per_project: 'Cantidad máxima de nodos que un agente MCP puede tener en un '
    + 'proyecto. Si lo supera, deja de crear nodos y avisa. Protege el canvas de un '
    + 'agente que se desboque.',
  max_credits_per_run: 'Créditos máximos que puede gastar una sola ejecución pedida por '
    + 'MCP. Si el plan supera el tope, el run no arranca.',
  max_credits_per_day: 'Créditos máximos que el MCP puede gastar en total por día '
    + '(ventana de 24 h). Tu saldo real se valida igual antes de cada nodo.',
  max_concurrent_runs: 'Cuántas ejecuciones MCP pueden estar corriendo al mismo tiempo. '
    + 'Con 1, un agente tiene que esperar a terminar antes de lanzar otra.',
};

const REQUESTABLE = [
  ['max_nodes_per_project', 'Máx. nodos por proyecto'],
  ['max_credits_per_run', 'Créditos por run'],
  ['max_credits_per_day', 'Créditos por día'],
  ['max_concurrent_runs', 'Runs simultáneos'],
];

const Help = ({ tip, testid }) => (
  <span className="mcp-help" data-tip={tip} data-testid={testid}>
    <HelpCircle size={12} />
  </span>
);

const STATUS = {
  pending: { icon: Clock, label: 'Pendiente de aprobación' },
  approved: { icon: Check, label: 'Aprobada' },
  rejected: { icon: X, label: 'Rechazada' },
};

const RequestRow = ({ req }) => {
  const st = STATUS[req.status] || STATUS.pending;
  const Icon = st.icon;
  const label = REQUESTABLE.find(([k]) => k === req.limit_key)?.[1] || req.limit_key;
  return (
    <div className={`mcp-limit-req ${req.status}`} data-testid={`mcp-limit-req-${req.id}`}>
      <Icon size={11} />
      <span>{label}: {req.current_value ?? '—'} → {req.approved_value ?? req.requested_value}</span>
      <em>{st.label}</em>
      {req.admin_note && <small>“{req.admin_note}”</small>}
    </div>
  );
};

const McpLimitsBox = () => {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('max_nodes_per_project');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await mcpService.getLimits());
    } catch (e) {
      setState({ error: true });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const number = Number(value);
    if (!Number.isInteger(number) || number <= 0) {
      return toast.error('Poné un número entero mayor a cero');
    }
    setSending(true);
    try {
      await mcpService.requestLimitIncrease({ limitKey: key, value: number, reason });
      toast.success('Solicitud enviada — un administrador la va a revisar');
      setOpen(false);
      setValue('');
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || 'No se pudo enviar la solicitud');
    } finally {
      setSending(false);
    }
  };

  if (!state) {
    return (
      <div className="mcp-limits-box" data-testid="mcp-limits-loading">
        <Loader2 size={13} className="animate-spin" /> Cargando límites…
      </div>
    );
  }
  if (state.error) return null;

  const l = state.limits || {};
  const pending = (state.requests || []).filter((r) => r.status === 'pending');

  return (
    <div className="mcp-limits-box" data-testid="mcp-limits-box">
      <div className="mcp-limits-head">
        <ShieldCheck size={13} />
        <strong>Límites de seguridad (sólo MCP)</strong>
        <Help tip={HELP.box} testid="mcp-limits-help" />
      </div>

      <div className="mcp-limits-grid">
        <span className="mcp-limit-pill" data-testid="mcp-limit-max-nodes">
          Máx. nodos por proyecto: <b>{l.max_nodes_per_project}</b>
          <Help tip={HELP.max_nodes_per_project} testid="mcp-help-max-nodes" />
        </span>
        <span className="mcp-limit-pill" data-testid="mcp-limit-budget">
          <b>{l.max_credits_per_run}</b>/run
          <Help tip={HELP.max_credits_per_run} testid="mcp-help-per-run" />
          · <b>{l.max_credits_per_day}</b>/día
          <Help tip={HELP.max_credits_per_day} testid="mcp-help-per-day" />
          · <b>{l.max_concurrent_runs}</b> run{l.max_concurrent_runs === 1 ? '' : 's'}
          <Help tip={HELP.max_concurrent_runs} testid="mcp-help-concurrent" />
        </span>
      </div>

      {(state.requests || []).length > 0 && (
        <div className="mcp-limit-reqs" data-testid="mcp-limit-requests">
          {state.requests.slice(0, 3).map((r) => <RequestRow key={r.id} req={r} />)}
        </div>
      )}

      {state.requests_table_missing ? (
        <p className="mcp-limits-note" data-testid="mcp-limit-requests-missing">
          Para pedir un aumento falta la migración <code>{state.requests_migration}</code>.
        </p>
      ) : !open ? (
        <button
          className="mcp-limits-ask"
          onClick={() => setOpen(true)}
          disabled={pending.length > 0}
          data-testid="mcp-request-increase-open"
        >
          <Send size={11} />
          {pending.length > 0 ? 'Ya tenés una solicitud pendiente' : 'Solicitar aumento'}
        </button>
      ) : (
        <div className="mcp-limits-form" data-testid="mcp-request-increase-form">
          <select
            value={key}
            onChange={(e) => setKey(e.target.value)}
            data-testid="mcp-request-limit-key"
          >
            {REQUESTABLE.map(([k, label]) => (
              <option key={k} value={k}>{label} (actual {l[k]})</option>
            ))}
          </select>
          <input
            type="number"
            min="1"
            placeholder="Nuevo valor"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="mcp-request-limit-value"
          />
          <input
            type="text"
            placeholder="Motivo (opcional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="mcp-request-limit-reason"
          />
          <div className="mcp-limits-form-actions">
            <button onClick={submit} disabled={sending} data-testid="mcp-request-limit-submit">
              {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              Enviar
            </button>
            <button
              className="ghost"
              onClick={() => setOpen(false)}
              data-testid="mcp-request-limit-cancel"
            >
              Cancelar
            </button>
          </div>
          <em>La autoriza un administrador desde el panel de Administración.</em>
        </div>
      )}
    </div>
  );
};

export default McpLimitsBox;
